import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterNavLink, Link as RouterLink, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import {
  makeStyles,
  AppBar,
  Toolbar,
  Typography,
  Link,
  IconButton,
  Drawer,
  Hidden,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';

import UserMenu from '../user/userMenu';
import NavBarSelect from './NavBarSelect';
import PinList from '../pins/PinList';

import { selectItemOwners, selectCurrentItemOwner, changeItemOwner } from '../itemOwner/itemOwnersSlice';
import { selectCountriesWithNames, selectCurrentCountry } from '../countries/countriesSlice';

import logo from '../../assets/img/logo-NokianTyres.svg';

const navbarStyles = makeStyles((theme) => ({
  appBar: {
    boxShadow: '2px 4px 10px 0 rgba(0,0,0,0.12)',
    backgroundColor: '#fff',
    color: '#000',
  },
  toolbar: {
    flexWrap: 'wrap',
    display: 'flex',
  },
  logo: {
    marginRight: '45px'
  },
  titleLink: {
    marginRight: 'auto',
    textDecoration: 'none'
  },
  title: {
    color: theme.palette.primary.main,
    fontFamily: theme.brandFont,
    fontWeight: 'bold',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',

    // Some hacking to control long/short name changes
    '& $titleLong': {
      display: 'none'
    },

    '@media (min-width: 540px)': {
      '& $titleLong': {
        display: 'inline'
      },
      '& $titleShort': {
        display: 'none'
      }
    },
    '@media (min-width: 960px)': {
      '& $titleLong': {
        display: 'none'
      }
    },
    '@media (min-width: 1170px)': {
      '& $titleShort': {
        display: 'inline'
      }
    },
    '@media (min-width: 1400px)': {
      '& $titleLong': {
        display: 'inline'
      },
      '& $titleShort': {
        display: 'none'
      }
    }
  },
  titleLong: {},
  titleShort: {},
  nav: {
    marginLeft: '1rem',
    marginRight: '1rem',
    '& ul': {
      display: 'flex',
      listStyle: 'none',
      margin: 0,
      padding: 0
    },
    '& li': {
      marginLeft: '1rem',
      marginRight: '1rem'
    },
    '& a.active': {
      textDecoration: 'underline'
    }
  },
  countrySelector: {
    marginLeft: '1rem',
    marginRight: '1rem',

  },
  menuButton: {

  },
  closeMenuButton: {
    marginRight: 'auto',
    marginLeft: 0,
  },
  drawerSelect: {
    width: '100%',
    marginBottom: theme.spacing(2)
  }
}));

const NavBar = () => {
  const classes = navbarStyles();
  const { t } = useTranslation();

  const owners = useSelector(selectItemOwners);
  const selectedOwner = useSelector(selectCurrentItemOwner);
  const countries = useSelector(selectCountriesWithNames);
  const selectedCountry = useSelector(selectCurrentCountry);

  const dispatch = useDispatch();
  const history = useHistory();

  const [ mobileMenuOpen, setMobileMenuOpen ] = React.useState(false);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if(isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Owner changed, redirect to front page (but not on first render)
      console.log('navbar useeffect owner change to ', selectedOwner);
      history.push(`/pih/owner/${selectedOwner}/items`);
    }
  }, [selectedOwner]);

  const handleOwnerChange = event => {
    dispatch(changeItemOwner(event.target.value));
  }

  const handleCountryChange = event => {
    const pathParts = window.location.pathname.split('/');
    // Language prefix is always second
    pathParts[1] = event.target.value;
    const newPath = pathParts.join('/');
    // Redirect to another stage language version
    window.location.href = newPath + window.location.hash;
  }

  const handleDrawerToggle = event => {
    setMobileMenuOpen(!mobileMenuOpen);
  }

  const ownerOptions = owners.map(item => ({label: item, value: item}));
  const countriesOptions = countries.map(item => ({label: item.name, value: item.id}));

  const nav = {
    PC1: [
      {name: t('nav:itemFamilies'), url: `/pih/owner/${selectedOwner}/families`},
      {name: t('nav:items'), url: `/pih/owner/${selectedOwner}/items`},
      {name: t('nav:feeds'), url: `/pih/owner/${selectedOwner}/feeds`},
      {name: t('nav:savedSearches'), url: `/pih/owner/${selectedOwner}/saved-searches`},
      {name: t('nav:tyreAwards'), url: `/pih/owner/${selectedOwner}/tyre-awards`},
      {name: t('nav:catalog'), url: `/pih/owner/${selectedOwner}/catalog`},
    ],
    PC24: [
      {name: t('nav:itemFamilies'), url: `/pih/owner/${selectedOwner}/families`},
      {name: t('nav:treads'), url: `/pih/owner/${selectedOwner}/treads`},
      {name: t('nav:items'), url: `/pih/owner/${selectedOwner}/items`},
      {name: t('nav:wheels'), url: `/pih/owner/${selectedOwner}/wheels`},
      {name: t('nav:feeds'), url: `/pih/owner/${selectedOwner}/feeds`},
      {name: t('nav:savedSearches'), url: `/pih/owner/${selectedOwner}/saved-searches`},
      {name: t('nav:catalog'), url: `/pih/owner/${selectedOwner}/catalog`},
    ],
  };

  const navLinks = nav[selectedOwner];

  return (
    <>
      <AppBar className={classes.appBar} position="static">
        <Toolbar className={classes.toolBar}>

          <div className={classes.logo}><RouterLink to="/"><img src={logo} alt="Nokian Tyres logo" /></RouterLink></div>

          <RouterLink to="/" className={classes.titleLink}>
            <Typography component="span" className={classes.title}>
              <span className={classes.titleLong}>{t('app:name')}</span>
              <span className={classes.titleShort} title={t('app:name')}>{t('app:shortName')}</span>
            </Typography>
          </RouterLink>

          <Hidden lgUp>
            <IconButton
              color="inherit"
              aria-label="Open menu"
              edge="start"
              onClick={handleDrawerToggle}
              className={classes.menuButton}
            >
              <MenuIcon />
            </IconButton>
          </Hidden>

          <Hidden mdDown>
            <nav className={classes.nav}>
              <ul>
                {navLinks.map(item => (
                  <li key={item.name}><Link variant="subtitle1" color="inherit" component={RouterNavLink} to={item.url}>{item.name}</Link></li>
                ))}
              </ul>
            </nav>

            <NavBarSelect id="owner-select" options={ownerOptions} selectedOption={selectedOwner} handleChange={handleOwnerChange} />

            <NavBarSelect id="country-select" options={countriesOptions} selectedOption={selectedCountry} handleChange={handleCountryChange} className={classes.countrySelector} />

            <UserMenu className={classes.userMenu} withButton />
          </Hidden>
        </Toolbar>
      </AppBar>

      <Hidden mdUp>
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <IconButton onClick={handleDrawerToggle} className={classes.closeMenuButton}>
            <CloseIcon />
          </IconButton>

          <div className={classes.drawerWrap}>

            <List>
              {navLinks.map(item => (
                <ListItem button key={item.name} component={RouterLink} to={item.url} onClick={handleDrawerToggle}>
                  <ListItemText primary={item.name} />
                </ListItem>
              ))}
            </List>

            <Divider />

            <PinList />

            <Divider />

            <Box px={2} pt={2}>
              <NavBarSelect id="owner-select" options={ownerOptions} selectedOption={selectedOwner} handleChange={handleOwnerChange} className={classes.drawerSelect} />
              <NavBarSelect id="country-select" options={countriesOptions} selectedOption={selectedCountry} handleChange={handleCountryChange} className={classes.drawerSelect} />
            </Box>

            <Divider />

            <Box p={2}>
              <UserMenu className={classes.userMenu} />
            </Box>

          </div>
        </Drawer>
      </Hidden>
    </>
  );
};

export default NavBar;
